import type { ReactNode } from 'react';
import { COLORS } from '../../constants/theme';

interface QueueSummaryRowProps {
  /** Unique id for this row's expanded region — wired to `aria-controls`/the region's own `id`. */
  id: string;
  label: string;
  /** Caller-supplied delta string — Training renders `+601`, Construction renders `+2%`. Genuinely
   *  different formatting per queue type, not reusable as one hardcoded format. */
  delta: string;
  timeLabel: string;
  expanded: boolean;
  onToggle: () => void;
  /** The raw per-batch list, rendered only while expanded. */
  children?: ReactNode;
}

/**
 * SF-179 Component 5: presentational collapsed/expanded queue disclosure row, shared by Training's
 * mobile "In Training" card and Construction's mobile "Under Construction" card — real
 * `<button aria-expanded aria-controls>`, 44px min-height, `{expanded && ...}` conditional
 * rendering (not CSS `display:none`, per CLAUDE.md's collapsible-panel convention) for the
 * expanded raw-batch list.
 */
export function QueueSummaryRow({ id, label, delta, timeLabel, expanded, onToggle, children }: QueueSummaryRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={onToggle}
        className="flex items-center justify-between gap-2 text-xs w-full"
        style={{ minHeight: 44 }}
      >
        <span style={{ color: COLORS.text.primary }}>
          {label} <span style={{ color: COLORS.gold }}>{delta}</span>
        </span>
        <span className="font-mono text-[11px]" style={{ color: COLORS.text.tertiary }}>
          {timeLabel}
        </span>
      </button>
      {expanded && (
        <div id={id} className="flex flex-col gap-1 pl-2">
          {children}
        </div>
      )}
    </div>
  );
}
